import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  Slide
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import DropdownMenu from 'src/components/DropdownMenu';
import {
  PaymentFormList,
  PaymentComplementSeriesList,
  TransactionsType
} from 'src/utils/constants/transactions';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { CREATE_MANUAL_TRANSACTION } from 'src/store/actions/transactionActions';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'src/utils/dayjs';
import { LOAD_PENDING_PAYMENT_INCOME_CFDI } from 'src/store/constants/CFDI';
import { loadPendingPaymentIncomeCFDIsForClient } from 'src/store/actions/cfdiActions';
import TransactionDialogHeader from '../TransactionDialogHeader';
import CfdiAmountRowList from '../CfdiAmountRowList';
import FieldLabel from '../FieldLabel';
import makeInitialState from './initialState';
import validationSchema from './schema';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const invalidFieldSx = {
  '& .MuiFormLabel-root.Mui-error': { color: 'secondary.main' },
  '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: 'secondary.main'
  },
  '& .MuiFormHelperText-root.Mui-error': { color: 'secondary.main' }
};

const getInvoiceOptionLabel = (t, option) =>
  option
    ? `${t('COMMON.CONTRACT')} ${option.contract.folio} - ${t(
        'COMMON.SERIE'
      )} ${option.series} - ${option.folio} - $${formatAmountWithDecimals(
        option?.amountIvaIncluded
      )}`
    : '';

const GenerateManualPaymentDialog = ({ open, handleClose, submit, clientId }) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [cfdiList, setCfdiList] = useState([]);
  const loading = useSelector(
    isLoadingSelector([CREATE_MANUAL_TRANSACTION.action])
  );
  const isLoadingCfdis = useSelector(
    isLoadingSelector([LOAD_PENDING_PAYMENT_INCOME_CFDI.action])
  );

  const initialState = makeInitialState();
  const resetRef = useRef();
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      dispatch(loadPendingPaymentIncomeCFDIsForClient(clientId, setCfdiList));
    }
    return () => {
      if (resetRef.current) {
        resetRef.current();
      }
      setCfdiList([]);
    };
  }, [resetRef, dispatch, id, open]);

  const handleSubmitTransaction = useCallback(
    values => {
      const relatedInvoices = values.cfdis
        .filter(row => row.cfdi)
        .map(row => ({ invoiceId: row.cfdi.id, amount: row.amount }));

      // Don't reset here — the form must stay visible with its values while
      // the request is in flight. The dialog's own close effect resets the
      // form once it actually closes.
      submit(
        {
          relatedInvoices,
          paymentForm: values.paymentForm,
          series: values.series,
          date: values.date ? dayjs(values.date).toDate() : values.date,
          type: TransactionsType.PAYMENT
        },
        handleClose
      );
    },
    [submit, handleClose]
  );

  const handleCloseDialog = () => {
    handleClose();
    setCfdiList([]);
  };

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      maxWidth={false}
      sx={{ '& .MuiDialog-paper': { width: 680 } }}
      open={open}
      TransitionComponent={Transition}
      keepMounted
    >
      <TransactionDialogHeader
        title={t('COMMON.REGISTER_PAYMENT_COMPLEMENT')}
        subtitle={t('TRANSACTION.CREATE_PAYMENT_SUBTITLE')}
        onClose={handleCloseDialog}
      />
      <DialogContent style={{ marginBottom: '16px' }}>
        <Formik
          initialValues={initialState}
          validationSchema={validationSchema}
          onSubmit={handleSubmitTransaction}
          enableReinitialize
        >
          {({
            errors,
            handleSubmit,
            touched,
            values,
            resetForm,
            setFieldValue
          }) => {
            resetRef.current = () => resetForm();
            return (
              <form onSubmit={handleSubmit}>
                <FormControl fullWidth>
                  <CfdiAmountRowList
                    rows={values.cfdis}
                    availableCfdis={cfdiList}
                    isLoadingCfdis={isLoadingCfdis}
                    errors={errors.cfdis}
                    touched={Boolean(touched.cfdis)}
                    getOptionLabel={option => getInvoiceOptionLabel(t, option)}
                    onChange={rows => setFieldValue('cfdis', rows)}
                    cfdiLabel={t('COMMON.CFDI')}
                    totalLabel={t('TRANSACTION.TOTAL_AMOUNT')}
                  />
                </FormControl>

                <FieldLabel label={t('CFDI_FORM.PAYMENT_FORM')}>
                  <FormControl fullWidth>
                    <DropdownMenu
                      list={PaymentFormList}
                      value={values.paymentForm}
                      name="paymentForm"
                      setValue={value => setFieldValue('paymentForm', value)}
                      error={Boolean(touched.paymentForm && errors.paymentForm)}
                      errorText={touched.paymentForm && t(errors.paymentForm)}
                      sx={invalidFieldSx}
                    />
                  </FormControl>
                </FieldLabel>

                <FieldLabel label={t('COMMON.SERIE')}>
                  <FormControl fullWidth>
                    <DropdownMenu
                      list={PaymentComplementSeriesList}
                      value={values.series}
                      name="series"
                      setValue={value => setFieldValue('series', value)}
                      error={Boolean(touched.series && errors.series)}
                      errorText={touched.series && t(errors.series)}
                      sx={invalidFieldSx}
                    />
                  </FormControl>
                </FieldLabel>

                <FieldLabel label={t('TRANSACTION.DATE')}>
                  <DatePicker
                    name="date"
                    value={values.date || null}
                    onChange={newValue => {
                      setFieldValue('date', newValue);
                    }}
                    slotProps={{
                      textField: {
                        sx: { width: '100%' },
                        error: Boolean(touched.date && errors.date),
                        helperText: touched.date && t(errors.date),
                        FormHelperTextProps: { sx: { color: 'secondary.main' } },
                        InputProps: { autoComplete: 'off' }
                      }
                    }}
                  />
                </FieldLabel>

                <Divider sx={{ mt: 3, mb: 2 }} />

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1
                  }}
                >
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => {
                      resetForm(initialState);
                      handleCloseDialog();
                    }}
                    disabled={loading}
                  >
                    {t('COMMON.CANCEL')}
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={
                      loading ||
                      isLoadingCfdis ||
                      !values.cfdis.some(row => row.cfdi)
                    }
                    startIcon={
                      loading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : null
                    }
                  >
                    {loading ? t('COMMON.REGISTERING') : t('COMMON.REGISTER')}
                  </Button>
                </Box>
              </form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

GenerateManualPaymentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  clientId: PropTypes.number
};

export default GenerateManualPaymentDialog;
